import {
    DECISION_FIELDS_BY_PATH,
    OPERATORS_BY_VALUE,
} from '/b2b/dsarForms/routes/FormDetails/components/FormWorkflow/components/AddDecisionDialog/constants';
import {
    DSAR_ACTION_LABELS,
    DSAR_FIELD_TYPE_LABELS,
    DSAR_REQUEST_TYPE_AGENT_VERIFICATION_LEVEL_LABELS,
    DSAR_REQUEST_TYPE_GROUP_LABELS,
    DSAR_REQUEST_TYPE_JURISDICTION_LABELS,
    DSAR_REQUEST_TYPE_LABELS,
    DSAR_REQUEST_TYPE_VERIFICATION_LEVEL_LABELS,
} from '/b2b/dsarForms/constants';

import mapValues from 'lodash/mapValues';

/*
 * The vocabulary an audit change is read through: which i18n key names a path, a property or a
 * stored value. Data only, and it imports nothing from this folder, so both the renderer that
 * draws a change and the resolver that searches one can read it without going through each other.
 */

// Known change paths, named with the form editor's own labels. Workflow nodes share
// this map under `config.`; an unknown path is start-cased.
export const PATH_LABEL_KEYS = {
    'settings.orgIds': 'common.organizations',
    'settings.emailIngestionConfig.allowListEmails': 'dsar.emailSettings.allowListEmails.label',
    'settings.emailIngestionConfig.escalationEmails': 'dsar.emailSettings.escalationEmails.label',
    'settings.aiEmailParsing': 'dsar.emailSettings.aiEmailParsing.label',
    'settings.captchaEnabled': 'dsar.formSettings.captchaEnabled',
    'settings.duplicateReject': 'dsar.formSettings.duplicateReject',
    'settings.fromCompany': 'dsar.formSettings.companyName',
    'settings.fromName': 'dsar.formSettings.displayName',
    'settings.internalDueDays': 'dsar.formSettings.internalDueDate',
    'settings.ssoConfigId': 'dsar.formSettings.consumerSso.provider',
    'settings.ssoRequired': 'dsar.formSettings.consumerSso.required',
    'settings.unverifiedEmailReject': 'dsar.formSettings.unverifiedEmailReject',
    'styling.showHeader': 'dsar.formSettings.showFormName',
    'styling.stylesheetURL': 'common.fields.stylesheetUrl.label',
    'styling.instructions': 'common.instructions',
    /* Workflow node fields */
    'config.name': 'common.name',
    'config.assignees': 'workflows.audit.fields.assignees',
    'config.description': 'common.fields.description.label',
    'config.dataStoreId': 'dsar.formDetails.actions.dialog.associateDataStore',
    'config.actionConfig': 'workflows.audit.fields.actionConfig',
    'config.automationConfig': 'workflows.audit.fields.automationConfig',
    'config.connectionId': 'dsar.formDetails.processing.fields.integration',
    'config.connectionName': 'dsar.formDetails.processing.fields.integration',
    'config.externalDisplayName': 'workflows.audit.fields.externalDisplayName',
    'config.subprocessors': 'dsar.formDetails.notifyVendor.label',
    'config.rejectionReason': 'workflows.audit.fields.rejectionReason',
    'config.completionMessage': 'workflows.audit.fields.completionMessage',
    'config.notifyEmail': 'workflows.audit.fields.notifyEmail',
    'config.path': 'dsarWorkflows.decision.field.label',
    'config.branches': 'workflows.audit.fields.branches',
};

// The styling editor's colour labels. Mapped explicitly because some palette keys
// differ from their schema key (requiredColor → asterisk).
export const PALETTE_LABEL_KEYS = {
    primaryColor: 'dsar.formStyling.schema.primaryColor',
    errorColor: 'dsar.formStyling.schema.errorColor',
    fieldTextColor: 'dsar.formStyling.schema.fieldTextColor',
    helperTextColor: 'dsar.formStyling.schema.helperTextColor',
    backgroundColor: 'dsar.formStyling.schema.backgroundColor',
    formBackgroundColor: 'dsar.formStyling.schema.backgroundColor',
    asteriskColor: 'dsar.formStyling.schema.asteriskColor',
    requiredColor: 'dsar.formStyling.schema.asteriskColor',
};

// Field and request-type properties, named the way the editor names them. Without an
// entry a property keeps its start-cased segment, which is also untranslated.
export const FIELD_ATTR_LABEL_KEYS = {
    defaultValue: 'dsar.formFields.fields.defaultValue',
    displayOptions: 'dsar.formFields.fields.displayOptions.title',
    helperText: 'dsar.formFields.fields.helperText',
    label: 'common.fields.fieldLabel',
    // The parameter the answer is recorded under, not the label the data subject sees.
    name: 'dsar.formFields.details.fieldName',
    placeholder: 'dsar.formFields.fields.placeholder',
    position: 'common.order',
    readOnlyValue: 'dsar.formFields.fields.readOnlyValue',
    required: 'common.required',
    type: 'common.fields.type',
};

export const REQUEST_TYPE_ATTR_LABEL_KEYS = {
    agentSubmissionAllowed: 'dsar.formRequestTypes.agentVerificationEnabled.label',
    agentVerificationLevel: 'dsar.formRequestTypes.agentVerificationLevel.label',
    description: 'dsar.formRequestTypes.requestDescription.label',
    instructions: 'dsar.formRequestTypes.requestInstructions.label',
    jurisdiction: 'dsar.formRequestTypes.jurisdictions.label',
    ordinal: 'common.order',
    title: 'dsar.formRequestTypes.requestTitle.label',
    verificationLevel: 'dsar.formRequestTypes.verificationLevel.label',
};

// Template properties, named per type so the log reads like the dialog the admin used.
// An unknown type, or an older event carrying none, falls back to the start-cased segment.
export const TEMPLATE_ATTR_LABEL_KEYS = {
    EMAIL: {
        header: 'dsar.templates.editEmailDialog.email.header.label',
        subject: 'dsar.templates.editEmailDialog.email.subject.label',
    },
    PORTAL: {
        body: 'dsar.templates.editPortalDialog.portal.body.label',
        name: 'dsar.templates.editPortalDialog.portal.name.label',
    },
    WEB: {
        footer: 'dsar.templates.editWebDialog.web.footer.label',
        header: 'dsar.templates.editWebDialog.web.header.label',
    },
};

// UI labels for a field's Display Options state (the backend sends a stable token).
export const DISPLAY_OPTION_LABEL_KEYS = {
    all: 'dsar.formFields.fields.displayOptions.enabledAll',
    specific: 'dsar.formFields.fields.displayOptions.hasRequestConditions',
    disabled: 'dsar.formFields.fields.displayOptions.disabled',
};

// Stored value → i18n key for the properties that hold an enum, keyed by property so
// free text that reads like a stored state is never renamed.
export const VALUE_LABEL_KEYS_BY_ATTR = {
    agentVerificationLevel: DSAR_REQUEST_TYPE_AGENT_VERIFICATION_LEVEL_LABELS,
    jurisdiction: DSAR_REQUEST_TYPE_JURISDICTION_LABELS,
    type: DSAR_FIELD_TYPE_LABELS,
    verificationLevel: DSAR_REQUEST_TYPE_VERIFICATION_LEVEL_LABELS,
};

// A toggle reads as the state it leaves the setting in, as the workflow log does.
export const BOOLEAN_LABEL_KEYS = { false: 'common.disabled', true: 'common.enabled' };

// Stored value → i18n key for every enum a node config holds. The editor's own tables are
// reused verbatim, so rewording one carries into the log.
export const WORKFLOW_VALUE_LABEL_KEYS = {
    ...mapValues(DECISION_FIELDS_BY_PATH, 'label'),
    ...mapValues(OPERATORS_BY_VALUE, 'label'),
    // The static options a decision field offers: statuses, jurisdictions, origins.
    ...Object.assign(
        {},
        ...Object.values(DECISION_FIELDS_BY_PATH).map(field =>
            Object.fromEntries(
                (field.staticOptions ?? []).map(option => [option.value, option.label])
            )
        )
    ),
    ...DSAR_REQUEST_TYPE_LABELS,
    ...DSAR_REQUEST_TYPE_GROUP_LABELS,
    ...DSAR_ACTION_LABELS,
    true: 'common.enabled',
    false: 'common.disabled',
};

// Internal ids: meaningless to a reader and rewritten on every save, so no event shows them.
export const HIDDEN_FIELDS = new Set([
    'config.approvalId',
    'config.priority',
    'config.processingType',
    'config.rejectionReasonCode',
    'config.taskId',
]);

// Every i18n key a change label or value can resolve to, for the test that proves
// none of them is a typo.
export const CHANGE_LABEL_KEYS = [
    ...Object.values(PATH_LABEL_KEYS),
    ...Object.values(PALETTE_LABEL_KEYS),
    ...Object.values(FIELD_ATTR_LABEL_KEYS),
    ...Object.values(REQUEST_TYPE_ATTR_LABEL_KEYS),
    ...Object.values(TEMPLATE_ATTR_LABEL_KEYS).flatMap(labels => Object.values(labels)),
    ...Object.values(VALUE_LABEL_KEYS_BY_ATTR).flatMap(labels => Object.values(labels)),
    ...Object.values(BOOLEAN_LABEL_KEYS),
    ...Object.values(DISPLAY_OPTION_LABEL_KEYS),
];
